import type {
  AiCall,
  AiCallOrder,
  SearchAiCallsResponse,
} from "@pax/api-contract/ai/calls/v1alpha1";
import { focusHaloFlush, panelMotion } from "@pax/ui";
import { costOf, failed, hostOf, modelOf, violated } from "@/features/debug/model-call";
import { Failure } from "@/features/evals/components";
import { type CallFilter, iconOf, nameOf } from "./dimensions";
import { compact, seconds, usd } from "./measures";
import { Mark, Picker, Section } from "./pieces";
import {
  type CallShow,
  callShows,
  callSorts,
  type TimeWindow,
  useCallPages,
  when,
} from "./queries";

/** Estimated counts read as rounded, so they don't claim a precision they lack. */
function callCount(total: SearchAiCallsResponse["total"]): string {
  if (total.exact) return total.count.toLocaleString("en-US");
  return `about ${compact(Number(total.count.toPrecision(2)))}`;
}

function CallRow({
  call,
  agentName,
  onOpen,
}: {
  call: AiCall;
  agentName: string;
  onOpen: () => void;
}) {
  const durationMs = Date.parse(call.endedAt) - Date.parse(call.receivedAt);
  const model = modelOf(call);
  const host = hostOf(call);
  const cost = costOf(call);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`-mx-3 flex w-[calc(100%+1.5rem)] items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-foreground/5 ${panelMotion} ${focusHaloFlush}`}
    >
      <Mark src={iconOf("model", model)} size="size-5" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-sm">
          <span className="truncate font-medium">{model}</span>
          <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            <Mark src={iconOf("transport", call.transport)} size="size-3" />
            <span className="truncate">{nameOf("transport", call.transport)}</span>
          </span>
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {failed(call) ? (
            <span className="text-destructive">
              Failed{host ? ` at ${host}` : ""} · {call.errorType ?? "error"}
              {call.errorMessage ? `: ${call.errorMessage}` : ""}
            </span>
          ) : violated(call) ? (
            <span className="text-amber-700 dark:text-amber-400">
              Malformed · {call.errorType}
              {call.errorMessage ? `: ${call.errorMessage}` : ""}
            </span>
          ) : (
            [
              agentName,
              call.gameKind && (call.gameKind === "eval" ? "eval" : "game"),
              call.gameRound !== null && `round ${call.gameRound}`,
            ]
              .filter(Boolean)
              .join(" · ")
          )}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-sm tabular-nums">{cost === null ? "–" : usd(cost)}</span>
        <span className="mt-0.5 block text-xs whitespace-nowrap text-muted-foreground tabular-nums">
          {seconds(durationMs)} · {when(new Date(call.receivedAt))}
        </span>
      </span>
    </button>
  );
}

const emptyText: Record<CallShow, string> = {
  all: "No calls.",
  failed: "Nothing failed.",
  malformed: "Nothing came back malformed.",
};

function ShowToggle({ show, onShow }: { show: CallShow; onShow: (show: CallShow) => void }) {
  return (
    <div className="flex items-center gap-0.5">
      {(Object.keys(callShows) as CallShow[]).map(id => (
        <button
          key={id}
          type="button"
          aria-pressed={id === show}
          onClick={() => onShow(id)}
          className={`h-7 rounded-full px-2.5 text-xs ${
            id === show
              ? "bg-foreground/[0.07] font-medium text-foreground"
              : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
          } ${panelMotion} ${focusHaloFlush}`}
        >
          {callShows[id]}
        </button>
      ))}
    </div>
  );
}

/**
 * The calls a question matches, a page at a time, with how many there are beside the label.
 * The header sorts them and narrows them to one outcome.
 */
export function CallList({
  filter,
  window,
  sort,
  show,
  onSort,
  onShow,
  pageSize,
  onOpen,
}: {
  filter: CallFilter;
  window: TimeWindow;
  sort: AiCallOrder;
  show: CallShow;
  onSort: (sort: AiCallOrder) => void;
  onShow: (show: CallShow) => void;
  pageSize: number;
  onOpen: (call: AiCall) => void;
}) {
  const outcomePinned = filter.keep.outcome !== undefined;
  const shown = outcomePinned || show === "all" ? "all" : show;
  const searched: CallFilter =
    shown === "all" ? filter : { ...filter, keep: { ...filter.keep, outcome: [shown] } };
  const pages = useCallPages(searched, window, sort, pageSize);
  const askedBy = (call: AiCall) =>
    call.agentVersionId
      ? nameOf("agentVersion", call.agentVersionId)
      : nameOf("purpose", call.purpose);
  const calls = pages.data?.pages.flatMap(page => page.items) ?? [];
  const total = pages.data?.pages[0]?.total;
  return (
    <Section
      label={
        <>
          Calls
          {total && (
            <span className="ml-1.5 text-muted-foreground/60 tabular-nums">{callCount(total)}</span>
          )}
        </>
      }
      action={
        <div className="-mr-3 flex items-center gap-1">
          {!outcomePinned && <ShowToggle show={show} onShow={onShow} />}
          <Picker
            label={callSorts[sort]}
            groups={[
              (Object.keys(callSorts) as AiCallOrder[]).map(id => ({
                id,
                label: callSorts[id],
                selected: id === sort,
              })),
            ]}
            onPick={id => onSort(id as AiCallOrder)}
          />
        </div>
      }
    >
      <Failure error={pages.error} />
      {pages.isPending && <p className="py-2 text-sm text-muted-foreground">Reading calls…</p>}
      {calls.map(call => (
        <CallRow
          key={call.callId}
          call={call}
          agentName={askedBy(call)}
          onOpen={() => onOpen(call)}
        />
      ))}
      {pages.isSuccess && calls.length === 0 && (
        <p className="py-2 text-sm text-muted-foreground">{emptyText[shown]}</p>
      )}
      {pages.hasNextPage && (
        <button
          type="button"
          disabled={pages.isFetchingNextPage}
          onClick={() => void pages.fetchNextPage()}
          className={`-mx-3 mt-1 w-[calc(100%+1.5rem)] rounded-xl px-3 py-2.5 text-sm text-muted-foreground hover:bg-foreground/5 hover:text-foreground ${panelMotion} ${focusHaloFlush}`}
        >
          {pages.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </Section>
  );
}
